// src/oura/publish/samplePage.ts
//
// The Pages workshop's page for the sample business nyonya-table, on the dev
// server only (H20a, scope 2.23). The signature page (/tasting-menu) carries
// the draft a person started from the client's answer in the Answer Plan.
// A page on the plan is refused for the client's yes; a page off it carries
// Oura's own draft, refused as not on the plan. Every other
// fixture business keeps the generic workshop sample. Invented content,
// nyonya-table only. DELETE with the other sample stores.

import { SAMPLE_DRAFT_OFF_PLAN, SAMPLE_DRAFT_ON_PLAN, sampleOnPlan } from "@service/aeo/publishRefusal";
import { linkBudgetForRole, roleForUrl, type PageWorkshop } from "../pageWorkshop";

/** The doc the words come from, as the refusal names it. */
export const SAMPLE_PAGE_SOURCE = "Answer Plan (S1, S3, B1, G4)";

/** nyonya-table's page in the workshop, with the sample's draft id. */
export function nyonyaSamplePage(url: string, title: string): PageWorkshop {
    const role = roleForUrl(url);
    if (!sampleOnPlan(url)) return plainPage(url, title, SAMPLE_DRAFT_OFF_PLAN);
    if (!/\/tasting-menu\/?$/.test(url)) return plainPage(url, title, SAMPLE_DRAFT_ON_PLAN);
    return {
        url,
        title: title || "Tasting menu",
        role,
        linkBudget: linkBudgetForRole(role),
        draftId: SAMPLE_DRAFT_ON_PLAN,
        metrics: null,
        voice: {
            summary: "Warm and plain. Names the dish before the story, and says how to book.",
            sampledFrom: ["/", "/menu", "/about"],
        },
        history: [],
        blocks: [
            { id: "n1", tag: "h1", text: "Tasting menu" },
            { id: "n2", tag: "p", text: "A menu of our favourite dishes, cooked fresh every day." },
            { id: "n3", tag: "h2", text: "How it works" },
            { id: "n4", tag: "p", text: "Eight courses, served for the table to share. Book 2 days ahead for groups of 6 or more." },
            { id: "n5", tag: "h2", text: "Opening hours" },
            { id: "n6", tag: "p", text: "Tuesday to Sunday, 11.30am to 9.30pm. Closed on Mondays." },
        ],
        recommendations: [
            {
                kind: "copy",
                id: "n-r1",
                blockId: "n2",
                before: "A menu of our favourite dishes, cooked fresh every day.",
                after: "Our signature dishes: ayam buah keluak, nyonya laksa, chap chye and kueh pie tee, cooked fresh every day.",
                justification: {
                    reason: `Siew Ling named these 4 dishes in her answer to Q3 in the ${SAMPLE_PAGE_SOURCE}. The page names none of them today.`,
                    expected: "The page can be matched to a search for any of the 4 dishes by name.",
                },
            },
        ],
    };
}

/** Any other page: Oura's own draft from the crawl. Off the plan, its publish is refused as not on the plan. */
function plainPage(url: string, title: string, draftId: string): PageWorkshop {
    const role = roleForUrl(url);
    const name = title || "This page";
    return {
        url,
        title: name,
        role,
        linkBudget: linkBudgetForRole(role),
        draftId,
        metrics: null,
        voice: {
            summary: "Warm and plain. Names the dish before the story, and says how to book.",
            sampledFrom: ["/", "/menu", "/about"],
        },
        history: [],
        blocks: [
            { id: "o1", tag: "h1", text: name },
            { id: "o2", tag: "p", text: "Welcome to Nyonya Table." },
        ],
        recommendations: [
            {
                kind: "copy",
                id: "o-r1",
                blockId: "o2",
                before: "Welcome to Nyonya Table.",
                after: "Nyonya Table cooks Peranakan food in Bukit Bintang and Mont Kiara.",
                justification: {
                    reason: "The line names no food and no place, so no search can be matched to it.",
                    expected: "The page can be matched to a search for Peranakan food in either area.",
                },
            },
        ],
    };
}
